import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Download, FileSpreadsheet, ArrowRightLeft, Info } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { downloadCsv, downloadXlsx } from "../lib/exporters";

/**
 * Long-Stay Reconciliation Report.
 *
 * One-off tool built to help finance start charging rent to HLC from a chosen
 * cut-off date (default: 2026-04-01). Lists every HLC container that is
 * currently sitting in the yard (last_status == IN), with a "days billable"
 * column that is capped at the cut-off date so containers that arrived AFTER
 * the cut-off are billed from their arrival day instead.
 *
 * Backend: GET /api/reports/long-stay?carrier_code=HLC&cutoff_date=YYYY-MM-DD
 */
export default function LongStayReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [carrierCode, setCarrierCode] = useState("HLC");
  const [cutoffDate, setCutoffDate] = useState("2026-04-01");
  const [yardCode, setYardCode] = useState("");
  const [minDays, setMinDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rep, setRep] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    if (!carrierCode || !cutoffDate) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        carrier_code: carrierCode,
        cutoff_date: cutoffDate,
      });
      if (yardCode) params.append("yard_code", yardCode);
      if (minDays) params.append("min_days", String(minDays));
      const { data } = await api.get(`/reports/long-stay?${params.toString()}`);
      setRep(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRep(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    if (!rep?.rows) return [];
    const needle = q.trim().toUpperCase();
    if (!needle) return rep.rows;
    return rep.rows.filter((r) =>
      (r.container_number || "").includes(needle) ||
      (r.yard || "").includes(needle) ||
      (r.iso_type || "").includes(needle)
    );
  }, [rep, q]);

  const columns = [
    { key: "container_number", label: "Container" },
    { key: "iso_type", label: "ISO type" },
    { key: "size", label: "Size" },
    { key: "yard", label: "Yard" },
    { key: "location_code", label: "Location" },
    { key: "grade", label: "Grade" },
    { key: "condition", label: "Condition" },
    { key: "gate_in_date", label: "Gate in" },
    { key: "in_before_cutoff", label: "In before cutoff", format: (v) => (v ? "Yes" : "No") },
    { key: "days_in_yard", label: "Days in yard" },
    { key: "rent_start_date", label: "Rent starts" },
    { key: "days_billable_since_cutoff", label: "Days billable" },
  ];

  const downloadName = `long-stay_${carrierCode}_${cutoffDate}_asof_${today}`;

  const kpi = (label, value, sub) => (
    <div className="border border-gray-200 bg-white px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div data-testid="long-stay-report">
      <PageHeader
        title="Long-stay reconciliation"
        subtitle="Rent-billing worksheet — containers still in the yard, ranked by dwell time."
        testid="long-stay-header"
        actions={(
          <div className="flex gap-2">
            <button className="ps-btn ps-btn-secondary" onClick={load} disabled={loading} data-testid="long-stay-refresh">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : <><RefreshCw className="w-4 h-4" /> Refresh</>}
            </button>
            <button
              className="ps-btn ps-btn-secondary"
              disabled={!filteredRows.length}
              onClick={() => downloadCsv(filteredRows, downloadName, columns)}
              data-testid="long-stay-csv"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              className="ps-btn ps-btn-primary"
              disabled={!filteredRows.length}
              onClick={() => downloadXlsx(filteredRows, downloadName, columns, "Long-stay")}
              data-testid="long-stay-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4" /> XLSX
            </button>
          </div>
        )}
      />

      <div className="border border-gray-200 bg-white p-4 mb-5" data-testid="long-stay-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier</label>
            <input value={carrierCode} onChange={(e) => setCarrierCode(e.target.value.toUpperCase())}
              className="ps-input font-mono w-full" data-testid="long-stay-carrier" maxLength={20} />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Rent start (cut-off)</label>
            <input type="date" value={cutoffDate} onChange={(e) => setCutoffDate(e.target.value)}
              className="ps-input font-mono w-full" data-testid="long-stay-cutoff" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Yard (optional)</label>
            <select value={yardCode} onChange={(e) => setYardCode(e.target.value)} className="ps-input font-mono w-full" data-testid="long-stay-yard">
              <option value="">All yards</option>
              <option value="TEMA">Tema (Main)</option>
              <option value="FISHHB">Fishingharbour</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Min days in yard</label>
            <input type="number" min={0} value={minDays} onChange={(e) => setMinDays(Number(e.target.value) || 0)}
              className="ps-input font-mono w-full" data-testid="long-stay-mindays" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Search</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Container / yard / ISO"
              className="ps-input font-mono w-full" data-testid="long-stay-search" />
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-gray-500">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            The report reads the <span className="font-mono">containers</span> cache (same source as Empty Stock).
            &quot;Days billable&quot; is capped by the cut-off — containers that arrived <em>after</em> the cut-off are billed from their gate-in date instead.
          </span>
        </div>
      </div>

      {rep && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="long-stay-kpis">
          {kpi("Containers in yard", rep.total, `Carrier: ${rep.carrier_code}`)}
          {kpi("Present on cut-off", rep.in_before_cutoff, `Rent from ${rep.cutoff_date}`)}
          {kpi("Report as-of", rep.as_of, "Days billable relative to today")}
          {kpi("Filtered rows", filteredRows.length, q || minDays ? "After filters" : "No filters applied")}
        </div>
      )}

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="long-stay-table">
          <thead>
            <tr>
              {columns.map((c) => <th key={c.key}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center py-10 text-gray-400">No containers match.</td></tr>
            )}
            {!loading && filteredRows.map((r) => (
              <tr key={r.container_number} data-testid={`long-stay-row-${r.container_number}`}>
                <td className="font-mono font-bold">{r.container_number}</td>
                <td className="font-mono text-xs">{r.iso_type || "—"}</td>
                <td className="text-xs">{r.size || "—"}</td>
                <td className="font-mono text-xs">{r.yard || "—"}</td>
                <td className="font-mono text-xs">{r.location_code || "—"}</td>
                <td className="text-xs">{r.grade || "—"}</td>
                <td className="text-xs">{r.condition || "—"}</td>
                <td className="font-mono text-xs">{r.gate_in_date || "—"}</td>
                <td className="text-xs">
                  {r.in_before_cutoff
                    ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-green-100 text-green-800 text-[10px] font-bold">YES</span>
                    : <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500 text-[10px] font-bold">NO</span>}
                </td>
                <td className="font-mono text-xs text-right">{r.days_in_yard ?? "—"}</td>
                <td className="font-mono text-xs">{r.rent_start_date || "—"}</td>
                <td className="font-mono text-xs text-right font-bold">{r.days_billable_since_cutoff ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-[11px] text-gray-500 font-mono">
        Showing {filteredRows.length} of {rep?.total ?? 0} row(s) · ordered by oldest gate-in first (most billable rent up top).
      </div>
    </div>
  );
}
